import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DashboardCard, StatusText } from "@/components/dashboard/dashboard-card";
import { ActionDialog, DialogNotice, DialogTextButton } from "@/components/common/ActionDialog";
import { UserAvatar } from "@/components/common/UserAvatar";
import { PhoneInput } from "@/components/PhoneInput/PhoneInput";
import {
  AsideRow,
  Field,
  FieldRow,
  SettingsGrid,
  SettingsSection,
  UnsavedBar,
  useSections,
} from "@/components/settings/settings-kit";
import { Flag } from "@/pages/brand/dashboard/affiliate-map";
import { useAffiliate } from "@/contexts/affiliate/hooks";
import { useDriver } from "@/contexts/driver";
import { passwordService } from "@/services/auth/password.service";
import { countries } from "@/constants/countries";
import { formatstorageUrl } from "@/utils/formatters";
import type { UpdateAffiliateDetails } from "@/types/affiliate";

type Gender = "MALE" | "FEMALE" | "OTHER";

interface Form {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  gender: Gender | "";
  age: string;
  country: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
}

const GENDERS: { value: Gender; label: string }[] = [
  { value: "FEMALE", label: "Female" },
  { value: "MALE", label: "Male" },
  { value: "OTHER", label: "Other" },
];

function countryName(code: string) {
  return countries.find((c) => c.code === code)?.name ?? code;
}

function DetailsSkeleton() {
  return (
    <SettingsGrid
      form={
        <>
          {[3, 2, 4].map((fields, i) => (
            <div key={i} className="rounded-lg border border-border bg-card">
              <div className="px-5 pb-3 pt-4 animate-pulse">
                <div className="h-4 w-28 rounded bg-muted" />
              </div>
              {i === 0 && (
                <div className="space-y-5 border-t border-border px-5 py-5 animate-pulse">
                  {Array.from({ length: fields }).map((_, j) => (
                    <div key={j} className="space-y-2">
                      <div className="h-3.5 w-24 rounded bg-muted" />
                      <div className="h-9 w-full rounded-lg bg-muted" />
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </>
      }
      aside={
        <div className="rounded-lg border border-border bg-card">
          <div className="px-5 pb-3 pt-4 animate-pulse">
            <div className="h-4 w-24 rounded bg-muted" />
          </div>
          <div className="flex items-center gap-4 px-5 pb-4 animate-pulse">
            <div className="size-16 rounded-full bg-muted" />
            <div className="space-y-2">
              <div className="h-4 w-36 rounded bg-muted" />
              <div className="h-3 w-48 rounded bg-muted" />
            </div>
          </div>
        </div>
      }
    />
  );
}

/**
 * Who the affiliate is: name and phone, a few facts about them, where they
 * live, and how they sign in. The aside is the account card brands and
 * payouts use, redrawn from the form as it changes.
 */
export function PersonalInfo() {
  const { details, isLoading, error, updateDetails } = useAffiliate();
  const { isTourOpen } = useDriver();
  const sections = useSections("contact", isTourOpen);
  const [saving, setSaving] = useState(false);
  const [countryCode, setCountryCode] = useState("+1");
  const [form, setForm] = useState<Form>({
    firstName: "",
    lastName: "",
    phoneNumber: "",
    gender: "",
    age: "",
    country: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
  });

  const [emailOpen, setEmailOpen] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [changingEmail, setChangingEmail] = useState(false);

  const [passwordOpen, setPasswordOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current: "", next: "", confirm: "" });
  const [showPasswords, setShowPasswords] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [changingPassword, setChangingPassword] = useState(false);

  const fromDetails = (): Form => ({
    firstName: details?.firstName || "",
    lastName: details?.lastName || "",
    phoneNumber: details?.phoneNumber?.number || "",
    gender: (details?.gender as Gender | undefined) || "",
    age: details?.age ? String(details.age) : "",
    country: details?.country || "",
    address: details?.address || "",
    city: details?.city || "",
    state: details?.state || "",
    zipCode: details?.zipCode || "",
  });

  useEffect(() => {
    if (!details) return;
    setForm(fromDetails());
    if (details.phoneNumber?.countryCode) setCountryCode(details.phoneNumber.countryCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [details]);

  const hasChanges = useMemo(() => {
    if (!details) return false;
    const base = fromDetails();
    return (
      (Object.keys(base) as (keyof Form)[]).some((k) => form[k] !== base[k]) ||
      countryCode !== (details.phoneNumber?.countryCode || "+1")
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, countryCode, details]);

  const set = <K extends keyof Form>(key: K, value: Form[K]) => setForm((prev) => ({ ...prev, [key]: value }));

  const reset = () => {
    setForm(fromDetails());
    setCountryCode(details?.phoneNumber?.countryCode || "+1");
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hasChanges) return;
    const ageNumber = form.age ? Number.parseInt(form.age, 10) : undefined;
    if (form.age && (!Number.isFinite(ageNumber) || ageNumber! < 18 || ageNumber! > 100)) {
      toast.error("Age must be between 18 and 100");
      return;
    }
    setSaving(true);
    try {
      const number = form.phoneNumber.replace(/\D/g, "");
      const body: UpdateAffiliateDetails = {
        firstName: form.firstName,
        lastName: form.lastName,
        gender: form.gender || undefined,
        age: form.age ? ageNumber! : null,
        country: form.country,
        city: form.city,
        state: form.state,
        zipCode: form.zipCode,
        address: form.address,
        phoneNumber: number ? { countryCode, number, fullNumber: `${countryCode}${number}` } : undefined,
      };
      await updateDetails(body);
      toast.success("Details saved");
    } catch {
      toast.error("Could not save your details");
    } finally {
      setSaving(false);
    }
  };

  const closeEmail = () => {
    setEmailOpen(false);
    setNewEmail("");
    setEmailError(null);
  };

  const changeEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setChangingEmail(true);
    setEmailError(null);
    try {
      await updateDetails({ email: newEmail.trim() });
      toast.success("Email changed. Verify the new address from the code we sent");
      closeEmail();
    } catch (err) {
      setEmailError(err instanceof Error ? err.message : "Could not change the email");
    } finally {
      setChangingEmail(false);
    }
  };

  const closePassword = () => {
    setPasswordOpen(false);
    setPasswordForm({ current: "", next: "", confirm: "" });
    setShowPasswords(false);
    setPasswordError(null);
  };

  const changePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (passwordForm.next !== passwordForm.confirm) {
      setPasswordError("The new passwords do not match");
      return;
    }
    setChangingPassword(true);
    setPasswordError(null);
    try {
      await passwordService.changePassword(passwordForm.current, passwordForm.next, "affiliate");
      toast.success("Password changed");
      closePassword();
    } catch (err) {
      setPasswordError(err instanceof Error ? err.message : "Could not change the password");
    } finally {
      setChangingPassword(false);
    }
  };

  if (isLoading || !details) return <DetailsSkeleton />;

  const name = [form.firstName, form.lastName].filter(Boolean).join(" ").trim();
  const phone = form.phoneNumber ? `${countryCode} ${form.phoneNumber}` : null;

  return (
    <form onSubmit={save}>
      {error && (
        <div className="mb-4">
          <DialogNotice tone="error">{error}</DialogNotice>
        </div>
      )}
      <SettingsGrid
        form={
          <>
            <SettingsSection
              id="contact-section"
              title="Name and contact"
              open={sections.isOpen("contact")}
              onToggle={() => sections.toggle("contact")}
              bodyClassName="space-y-5"
            >
              <FieldRow>
                <Field id="SPF-firstName" label="First name" htmlFor="first-name">
                  <Input
                    id="first-name"
                    value={form.firstName}
                    onChange={(e) => set("firstName", e.target.value)}
                    autoComplete="given-name"
                  />
                </Field>
                <Field id="SPF-lastName" label="Last name" htmlFor="last-name">
                  <Input
                    id="last-name"
                    value={form.lastName}
                    onChange={(e) => set("lastName", e.target.value)}
                    autoComplete="family-name"
                  />
                </Field>
              </FieldRow>
              <Field id="SPF-phone" label="Phone" htmlFor="phone" help="Brands may use it to reach you about payouts">
                <PhoneInput
                  id="phone"
                  value={form.phoneNumber}
                  selectedCountryCode={countryCode}
                  onChange={(value, code) => {
                    setCountryCode(code);
                    set("phoneNumber", value);
                  }}
                />
              </Field>
            </SettingsSection>

            <SettingsSection
              id="about-section"
              title="About you"
              open={sections.isOpen("about")}
              onToggle={() => sections.toggle("about")}
              bodyClassName="space-y-5"
            >
              <FieldRow>
                <Field id="SPF-gender" label="Gender" htmlFor="gender">
                  <Select value={form.gender} onValueChange={(v) => v && set("gender", v as Gender)}>
                    <SelectTrigger id="gender">
                      <SelectValue placeholder="Choose" />
                    </SelectTrigger>
                    <SelectContent>
                      {GENDERS.map((g) => (
                        <SelectItem key={g.value} value={g.value}>
                          {g.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field id="SPF-age" label="Age" htmlFor="age">
                  <Input
                    id="age"
                    type="number"
                    inputMode="numeric"
                    min={18}
                    max={100}
                    value={form.age}
                    onChange={(e) => set("age", e.target.value)}
                    className="tabular-nums"
                  />
                </Field>
              </FieldRow>
            </SettingsSection>

            <SettingsSection
              id="address-section"
              title="Address"
              open={sections.isOpen("address")}
              onToggle={() => sections.toggle("address")}
              bodyClassName="space-y-5"
            >
              <Field id="SPF-country" label="Country" htmlFor="country">
                <Select value={form.country} onValueChange={(v) => v && set("country", v)}>
                  <SelectTrigger id="country">
                    <SelectValue placeholder="Choose a country" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {countries.map((c) => (
                      <SelectItem key={c.code} value={c.code}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <div id="SPF-address" className="space-y-5">
                <Field label="Street address" htmlFor="address">
                  <Input id="address" value={form.address} onChange={(e) => set("address", e.target.value)} autoComplete="street-address" />
                </Field>
                <div className="grid gap-4 sm:grid-cols-3">
                  <Field label="City" htmlFor="city">
                    <Input id="city" value={form.city} onChange={(e) => set("city", e.target.value)} autoComplete="address-level2" />
                  </Field>
                  <Field label="State" htmlFor="state">
                    <Input id="state" value={form.state} onChange={(e) => set("state", e.target.value)} autoComplete="address-level1" />
                  </Field>
                  <Field label="Postal code" htmlFor="zip">
                    <Input id="zip" value={form.zipCode} onChange={(e) => set("zipCode", e.target.value)} autoComplete="postal-code" className="tabular-nums" />
                  </Field>
                </div>
              </div>
            </SettingsSection>

            <SettingsSection
              id="sign-in-section"
              title="Sign-in"
              open={sections.isOpen("sign-in")}
              onToggle={() => sections.toggle("sign-in")}
              bodyClassName="space-y-5"
            >
              <div id="SPF-email" className="space-y-1.5">
                <Label htmlFor="email" className="text-foreground">
                  Email
                </Label>
                <div className="flex items-center justify-between gap-3 rounded-lg border border-input px-3 py-2">
                  <span id="email" className="truncate text-sm text-foreground">
                    {details.email}
                  </span>
                  <Button type="button" variant="outline" size="sm" onClick={() => setEmailOpen(true)}>
                    Change email
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">You sign in with this address. A new one needs verifying.</p>
              </div>
              <div id="SPF-password" className="space-y-1.5">
                <Label className="text-foreground">Password</Label>
                <div className="flex items-center justify-between gap-3 rounded-lg border border-input px-3 py-2">
                  <span className="text-sm tracking-widest text-muted-foreground">••••••••</span>
                  <Button type="button" variant="outline" size="sm" onClick={() => setPasswordOpen(true)}>
                    Change password
                  </Button>
                </div>
              </div>
            </SettingsSection>
          </>
        }
        aside={
          <>
            <DashboardCard title="Your account" bodyClassName="px-5 pb-2">
              <div className="flex items-center gap-4 pb-3">
                <UserAvatar src={formatstorageUrl(details.profileImage ?? "") || null} name={name} className="size-16 text-xl" />
                <div className="min-w-0">
                  <div className="truncate text-[15px] font-semibold text-foreground">{name || "Your name"}</div>
                  <div className="truncate text-xs text-muted-foreground">{details.email}</div>
                </div>
              </div>
              <AsideRow label="Email">
                <StatusText status={details.isEmailVerified ? "ACTIVE" : "PENDING"} label={details.isEmailVerified ? "Verified" : "Not verified"} />
              </AsideRow>
              <AsideRow label="Phone">{phone ? <span className="tabular-nums">{phone}</span> : "Not set"}</AsideRow>
              <AsideRow label="Country">
                {form.country ? (
                  <span className="inline-flex items-center gap-1.5">
                    <Flag code={form.country} />
                    {countryName(form.country)}
                  </span>
                ) : (
                  "Not set"
                )}
              </AsideRow>
              <AsideRow label="Address">
                {[form.city, form.state, form.zipCode].filter(Boolean).join(", ") || "Not set"}
              </AsideRow>
            </DashboardCard>
            <UnsavedBar visible={hasChanges} saving={saving} onCancel={reset} saveId="SPF-save-button" />
          </>
        }
      />

      <ActionDialog
        open={emailOpen}
        onClose={closeEmail}
        title="Change email"
        description="We send a code to the new address to verify it."
        footer={
          <>
            <DialogTextButton onClick={closeEmail} disabled={changingEmail}>
              Cancel
            </DialogTextButton>
            <Button type="submit" form="change-email-form" disabled={changingEmail || !newEmail.trim()}>
              Change email
            </Button>
          </>
        }
      >
        <form id="change-email-form" onSubmit={changeEmail} className="space-y-4">
          {emailError && <DialogNotice tone="error">{emailError}</DialogNotice>}
          <Field label="New email" htmlFor="new-email">
            <Input
              id="new-email"
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              autoComplete="email"
              required
            />
          </Field>
        </form>
      </ActionDialog>

      <ActionDialog
        open={passwordOpen}
        onClose={closePassword}
        title="Change password"
        footer={
          <>
            <DialogTextButton onClick={closePassword} disabled={changingPassword}>
              Cancel
            </DialogTextButton>
            <Button type="submit" form="change-password-form" disabled={changingPassword}>
              Change password
            </Button>
          </>
        }
      >
        <form id="change-password-form" onSubmit={changePassword} className="space-y-4">
          {passwordError && <DialogNotice tone="error">{passwordError}</DialogNotice>}
          <Field label="Current password" htmlFor="current-password">
            <Input
              id="current-password"
              type={showPasswords ? "text" : "password"}
              value={passwordForm.current}
              onChange={(e) => setPasswordForm({ ...passwordForm, current: e.target.value })}
              autoComplete="current-password"
              required
            />
          </Field>
          <Field label="New password" htmlFor="new-password">
            <Input
              id="new-password"
              type={showPasswords ? "text" : "password"}
              value={passwordForm.next}
              onChange={(e) => setPasswordForm({ ...passwordForm, next: e.target.value })}
              autoComplete="new-password"
              required
            />
          </Field>
          <Field label="Confirm new password" htmlFor="confirm-password">
            <Input
              id="confirm-password"
              type={showPasswords ? "text" : "password"}
              value={passwordForm.confirm}
              onChange={(e) => setPasswordForm({ ...passwordForm, confirm: e.target.value })}
              autoComplete="new-password"
              required
            />
          </Field>
          <div className="flex items-center gap-2">
            <Checkbox id="show-passwords" checked={showPasswords} onCheckedChange={(c) => setShowPasswords(c === true)} />
            <Label htmlFor="show-passwords" className="font-normal text-foreground">
              Show passwords
            </Label>
          </div>
        </form>
      </ActionDialog>
    </form>
  );
}
